import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChartBar, FaStar, FaFire } from 'react-icons/fa';
import { getGroupAssessmentData, getCourseAssessmentAverages, getCourseId } from '../../../firebase';
import RatingModificationHistory from '../../../components/common/RatingModificationHistory';
import { usePrivacy } from '../../../contexts/PrivacyContext';

const WorkAssessmentResultsPanel = ({ groupId, groupMembers = [] }) => {
    const { maskName, maskInitial } = usePrivacy();
    const [assessmentData, setAssessmentData] = useState(null);
    const [classAverages, setClassAverages] = useState({ volume: 0, quality: 0, importance: 0 });
    const [courseId, setCourseId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showTooltip, setShowTooltip] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
                : { key, direction: 'desc' }
        );
    };

    useEffect(() => {
        if (!groupId) {
            setIsLoading(false);
            return;
        }
        let cancelled = false;
        const fetchData = async () => {
            try {
                // Resolve courseId internally so the parent doesn't need extra state
                const resolvedCourseId = await getCourseId(groupId);
                if (cancelled) return;
                setCourseId(resolvedCourseId);

                const [data, courseAvgs] = await Promise.all([
                    getGroupAssessmentData(groupId, groupMembers),
                    resolvedCourseId
                        ? getCourseAssessmentAverages(resolvedCourseId)
                        : Promise.resolve({ volume: 0, quality: 0, importance: 0 })
                ]);
                console.log("WorkAssessmentResultsPanel - assessmentData:", data);
                console.log("WorkAssessmentResultsPanel - classAverages:", courseAvgs);
                if (cancelled) return;
                setAssessmentData(data);
                setClassAverages(courseAvgs);
            } catch (error) {
                console.error('Error fetching assessment data:', error);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [groupId, groupMembers]);

    if (isLoading) {
        return <LoadingContainer>Loading assessment data...</LoadingContainer>;
    }

    if (!assessmentData || assessmentData.totalEvents === 0) {
        return (
            <NoDataContainer>
                No activities found for this group yet.
            </NoDataContainer>
        );
    }

    const {
        totalEvents,
        memberProgress,
        averageScores,
        onTimeSubmissions,
        totalSubmissions,
        totalExpectedAssessments,
        latestDeadline
    } = assessmentData;

    // Calculate metrics
    const completionRate = totalExpectedAssessments > 0
        ? Math.round((totalSubmissions / totalExpectedAssessments) * 100)
        : 0;

    const totalCompletedAssessments = memberProgress.reduce((sum, m) => sum + m.completed, 0);
    const totalPossibleAssessments = memberProgress.reduce((sum, m) => sum + m.total, 0);

    const avgScore = ((parseFloat(averageScores.volume) + parseFloat(averageScores.quality) + parseFloat(averageScores.importance)) / 3).toFixed(1);

    const getSortValue = (member, key) => {
        switch (key) {
            case 'name': return (member.name || member.email || '').toLowerCase();
            case 'completed': return member.receivedCount ?? -1;
            case 'importanceTotal': return member.scores?.importance.total ?? -1;
            case 'importanceAvg': return parseFloat(member.scores?.importance.avg) || -1;
            case 'qualityTotal': return member.scores?.quality.total ?? -1;
            case 'qualityAvg': return parseFloat(member.scores?.quality.avg) || -1;
            case 'volumeTotal': return member.scores?.volume.total ?? -1;
            case 'volumeAvg': return parseFloat(member.scores?.volume.avg) || -1;
            default: return 0;
        }
    };

    const sortedMembers = sortConfig.key
        ? [...memberProgress].sort((a, b) => {
            const aVal = getSortValue(a, sortConfig.key);
            const bVal = getSortValue(b, sortConfig.key);
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        })
        : memberProgress;

    const SortIcon = ({ colKey }) => {
        if (sortConfig.key !== colKey) return <SortArrow>↕</SortArrow>;
        return <SortArrow $active>{sortConfig.direction === 'asc' ? '↑' : '↓'}</SortArrow>;
    };

    const onTimeRate = totalSubmissions > 0
        ? Math.round((onTimeSubmissions / totalSubmissions) * 100)
        : 0;

    // Determine engagement level
    const getEngagementLevel = () => {
        if (completionRate >= 80) return { level: 'High', color: '#059669' };
        if (completionRate >= 50) return { level: 'Medium', color: '#d97706' };
        return { level: 'Low', color: '#dc2626' };
    };
    const engagement = getEngagementLevel();

    const formatDeadline = (date) => {
        if (!date) return null;
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const maxDistCount = Math.max(
        1,
        ...sortedMembers.flatMap(m =>
            [1, 2, 3, 4].flatMap(s => [
                m.givenScores?.importance.distribution?.[s] || 0,
                m.givenScores?.quality.distribution?.[s] || 0,
                m.givenScores?.volume.distribution?.[s] || 0,
            ])
        )
    );
    const yAxisMax = Math.max(4, Math.ceil(maxDistCount / 4) * 4);
    const yStep = yAxisMax / 4;

    return (
        <Container>
            <SummaryCardsContainer>
                <SummaryCard>
                    <CardLabel>
                        Overall Completion
                        <InfoIconContainer
                            onMouseEnter={() => setShowTooltip('completion')}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            <InfoIcon>ⓘ</InfoIcon>
                            {showTooltip === 'completion' && (
                                <Tooltip>
                                    Percentage of completed assessments out of all expected assessments.
                                    <br /><br />
                                    <strong>Calculation:</strong><br />
                                    (Total completed assessments / Total expected assessments) × 100%
                                </Tooltip>
                            )}
                        </InfoIconContainer>
                    </CardLabel>
                    <CardValue>{completionRate}%</CardValue>
                    <CardSubtext>{totalCompletedAssessments} of {totalPossibleAssessments} assessments</CardSubtext>
                </SummaryCard>

                <SummaryCard>
                    <CardLabel>
                        Team Average Score
                        <InfoIconContainer
                            onMouseEnter={() => setShowTooltip('average')}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            <InfoIcon>ⓘ</InfoIcon>
                            {showTooltip === 'average' && (
                                <Tooltip>
                                    Average of all three assessment dimensions (Volume, Quality, Nature).
                                    <br /><br />
                                    Each dimension is scored from 1-4.
                                    <br /><br />
                                    <strong>Calculation:</strong><br />
                                    (Volume + Quality + Nature) / 3
                                </Tooltip>
                            )}
                        </InfoIconContainer>
                    </CardLabel>
                    <CardValue>{avgScore}</CardValue>
                    <CardSubtext>Out of 4.0</CardSubtext>
                </SummaryCard>

                <SummaryCard>
                    <CardLabel>
                        On-Time Submissions
                        <InfoIconContainer
                            onMouseEnter={() => setShowTooltip('ontime')}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            <InfoIcon>ⓘ</InfoIcon>
                            {showTooltip === 'ontime' && (
                                <Tooltip>
                                    Percentage of assessments submitted before or on the deadline.
                                    <br /><br />
                                    <strong>Calculation:</strong><br />
                                    (On-time submissions / Total submissions) × 100%
                                </Tooltip>
                            )}
                        </InfoIconContainer>
                    </CardLabel>
                    <CardValue>{onTimeRate}%</CardValue>
                    <CardSubtext>{onTimeSubmissions} of {totalSubmissions} submissions</CardSubtext>
                </SummaryCard>

                <SummaryCard>
                    <CardLabel>
                        Team Engagement
                        <InfoIconContainer
                            onMouseEnter={() => setShowTooltip('engagement')}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            <InfoIcon>ⓘ</InfoIcon>
                            {showTooltip === 'engagement' && (
                                <Tooltip>
                                    Overall team participation level based on completion rate.
                                    <br /><br />
                                    <strong>Levels:</strong><br />
                                    • High ≥ 80%<br />
                                    • Medium ≥ 50%<br />
                                    • Low &lt; 50%
                                </Tooltip>
                            )}
                        </InfoIconContainer>
                    </CardLabel>
                    <CardValue>{engagement.level}</CardValue>
                    <CardSubtext>Above class average</CardSubtext>
                </SummaryCard>
            </SummaryCardsContainer>

            <Section>
                <SectionTitleContainer>
                    <SectionTitle>Assessments Given</SectionTitle>
                    <InfoIconContainer
                        onMouseEnter={() => setShowTooltip('memberChart')}
                        onMouseLeave={() => setShowTooltip(null)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showTooltip === 'memberChart' && (
                            <DimensionTooltip>
                                <strong>Assessments Given — Score Distribution:</strong>
                                <br /><br />
                                <strong>• X-axis (1–4):</strong> Score value this member assigned to teammates.
                                <br /><br />
                                <strong>• Y-axis (count):</strong> Number of times this member gave that score across all submitted assessments.
                                <br /><br />
                                <strong>• Colors:</strong> Blue = Nature, Green = Quality, Orange = Volume.
                                <br /><br />
                                <strong>• Submitted / Total (%):</strong> Number of assessments this member actually submitted out of the total expected, with completion percentage.
                                <br /><br />
                                Each dimension has 4 bars showing how often this member gave each score (1–4). Hover a bar to see the exact count.
                            </DimensionTooltip>
                        )}
                    </InfoIconContainer>
                </SectionTitleContainer>
                {latestDeadline && (
                    <DeadlineText>
                        Team members have to complete assessments until {formatDeadline(latestDeadline)}
                    </DeadlineText>
                )}
                <ChartLegend>
                    <LegendItem><LegendDot $color="#3b82f6" />Nature</LegendItem>
                    <LegendItem><LegendDot $color="#10b981" />Quality</LegendItem>
                    <LegendItem><LegendDot $color="#f59e0b" />Volume</LegendItem>
                </ChartLegend>
                <ChartWrapper>
                    <YAxis>
                        {[yAxisMax, yStep * 3, yStep * 2, yStep, 0].map((v, i) => (
                            <YLabel key={i}>{v}</YLabel>
                        ))}
                    </YAxis>
                    <ChartColumn>
                        <ChartArea>
                            <GridLines>
                                {[0, 1, 2, 3, 4].map(i => <GridLine key={i} />)}
                            </GridLines>
                            <BarsContainer>
                                {sortedMembers.map((member, index) => (
                                    <DistMemberGroup key={member.email || index}>
                                        {[
                                            { dim: 'importance', color: '#3b82f6', label: 'Nature' },
                                            { dim: 'quality',    color: '#10b981', label: 'Quality' },
                                            { dim: 'volume',     color: '#f59e0b', label: 'Volume' },
                                        ].map(({ dim, color, label }) => (
                                            <DimGroup key={dim}>
                                                {[1, 2, 3, 4].map(score => {
                                                    const count = member.givenScores?.[dim].distribution?.[score] || 0;
                                                    return (
                                                        <BarSlot key={score}>
                                                            {count > 0 && <BarCount>{count}</BarCount>}
                                                            <Bar
                                                                $color={color}
                                                                $height={(count / yAxisMax) * 100}
                                                            />
                                                        </BarSlot>
                                                    );
                                                })}
                                            </DimGroup>
                                        ))}
                                    </DistMemberGroup>
                                ))}
                            </BarsContainer>
                        </ChartArea>
                        <LabelsRow>
                            {sortedMembers.map((member, index) => (
                                <BarLabelGroup key={member.email || index}>
                                    <DimScoreLabelsRow>
                                        {[0, 1, 2].map(i => (
                                            <DimScoreLabels key={i}>
                                                {[1, 2, 3, 4].map(s => <ScoreScaleLabel key={s}>{s}</ScoreScaleLabel>)}
                                            </DimScoreLabels>
                                        ))}
                                    </DimScoreLabelsRow>
                                    <MemberBarAvatar $color={member.color}>{maskInitial(member.name) || member.initial}</MemberBarAvatar>
                                    <BarMemberName>{maskName(member.name || member.email)}</BarMemberName>
                                    <BarMemberGivenCount>
                                        {member.completed ?? 0}/{member.total ?? 0}
                                        {' '}({member.total > 0 ? Math.round((member.completed / member.total) * 100) : 0}%)
                                    </BarMemberGivenCount>
                                </BarLabelGroup>
                            ))}
                        </LabelsRow>
                    </ChartColumn>
                </ChartWrapper>
            </Section>

            <Section>
                <SectionTitleContainer>
                    <SectionTitle>Assessments Received</SectionTitle>
                    <InfoIconContainer
                        onMouseEnter={() => setShowTooltip('memberScores')}
                        onMouseLeave={() => setShowTooltip(null)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showTooltip === 'memberScores' && (
                            <DimensionTooltip>
                                <strong>Assessments Received:</strong>
                                <br /><br />
                                <strong>• #Received:</strong> Number of assessments this member actually received / total expected (events contributed to × group size).
                                <br /><br />
                                <strong>• Total Nature / Quality / Volume:</strong> Sum of scores this member received from teammates across all assessments. Each dimension is rated 1–4.
                                <br /><br />
                                <strong>• Avg Nature / Quality / Volume:</strong> Total ÷ number of times assessed (average score received per assessment).
                            </DimensionTooltip>
                        )}
                    </InfoIconContainer>
                </SectionTitleContainer>

                <ScoreTableWrapper>
                    <ScoreTable>
                        <ScoreTableHead>
                            <ScoreTableRow>
                                <ScoreThMember onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Member <SortIcon colKey="name" /></ScoreThMember>
                                <ScoreTh onClick={() => handleSort('completed')} style={{ cursor: 'pointer' }}>#Received <SortIcon colKey="completed" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('importanceTotal')} style={{ color: '#3b82f6', cursor: 'pointer' }}>Total Nature <SortIcon colKey="importanceTotal" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('importanceAvg')} style={{ color: '#3b82f6', cursor: 'pointer' }}>Avg Nature <SortIcon colKey="importanceAvg" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('qualityTotal')} style={{ color: '#10b981', cursor: 'pointer' }}>Total Quality <SortIcon colKey="qualityTotal" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('qualityAvg')} style={{ color: '#10b981', cursor: 'pointer' }}>Avg Quality <SortIcon colKey="qualityAvg" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('volumeTotal')} style={{ color: '#f59e0b', cursor: 'pointer' }}>Total Volume <SortIcon colKey="volumeTotal" /></ScoreTh>
                                <ScoreTh onClick={() => handleSort('volumeAvg')} style={{ color: '#f59e0b', cursor: 'pointer' }}>Avg Volume <SortIcon colKey="volumeAvg" /></ScoreTh>
                            </ScoreTableRow>
                        </ScoreTableHead>
                        <tbody>
                            {sortedMembers.map((member, index) => {
                                return (
                                    <ScoreTableRow key={member.email || index}>
                                        <ScoreTdMember>
                                            <MemberCellInner>
                                                <MemberAvatar $color={member.color}>{maskInitial(member.name) || member.initial}</MemberAvatar>
                                                <MemberName>{maskName(member.name || member.email)}</MemberName>
                                            </MemberCellInner>
                                        </ScoreTdMember>
                                        <ScoreTd style={{ fontWeight: 700 }}>
                                            {member.receivedCount ?? '-'}/{member.expectedReceivedCount ?? '-'}
                                        </ScoreTd>
                                        <ScoreTd style={{ color: '#3b82f6' }}>{member.scores?.importance.total ?? '-'}</ScoreTd>
                                        <ScoreTd style={{ color: '#3b82f6' }}>{member.scores?.importance.avg ?? '-'}</ScoreTd>
                                        <ScoreTd style={{ color: '#10b981' }}>{member.scores?.quality.total ?? '-'}</ScoreTd>
                                        <ScoreTd style={{ color: '#10b981' }}>{member.scores?.quality.avg ?? '-'}</ScoreTd>
                                        <ScoreTd style={{ color: '#f59e0b' }}>{member.scores?.volume.total ?? '-'}</ScoreTd>
                                        <ScoreTd style={{ color: '#f59e0b' }}>{member.scores?.volume.avg ?? '-'}</ScoreTd>
                                    </ScoreTableRow>
                                );
                            })}
                        </tbody>
                    </ScoreTable>
                </ScoreTableWrapper>
            </Section>

            <Section>
                <SectionTitleContainer>
                    <SectionTitle>Team Assessment Dimensions</SectionTitle>
                    <InfoIconContainer
                        onMouseEnter={() => setShowTooltip('dimensions')}
                        onMouseLeave={() => setShowTooltip(null)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showTooltip === 'dimensions' && (
                            <DimensionTooltip>
                                <strong>Assessment Dimensions:</strong>
                                <br /><br />
                                <strong>• Nature:</strong> How critical this work was to the project's success
                                <br /><br />
                                <strong>• Quality:</strong> The thoroughness and excellence of the work delivered
                                <br /><br />
                                <strong>• Volume:</strong> The amount of effort and work contributed
                                <br /><br />
                                Each dimension is rated 1-4 by team members.
                            </DimensionTooltip>
                        )}
                    </InfoIconContainer>
                </SectionTitleContainer>
                <DimensionsGridWrapper>
                    <DimensionsGrid>
                        <DimensionCard>
                            <DimensionHeader>
                                <DimensionIcon $color="#3b82f6"><FaFire /></DimensionIcon>
                                <DimensionName>Nature</DimensionName>
                            </DimensionHeader>
                            <DimensionScore $color="#3b82f6">{averageScores.importance}</DimensionScore>
                            <DimensionComparison>
                                Team avg: {averageScores.importance} | Class avg: {classAverages.importance}
                            </DimensionComparison>
                        </DimensionCard>

                        <DimensionCard>
                            <DimensionHeader>
                                <DimensionIcon $color="#10b981"><FaStar /></DimensionIcon>
                                <DimensionName>Quality</DimensionName>
                            </DimensionHeader>
                            <DimensionScore $color="#10b981">{averageScores.quality}</DimensionScore>
                            <DimensionComparison>
                                Team avg: {averageScores.quality} | Class avg: {classAverages.quality}
                            </DimensionComparison>
                        </DimensionCard>

                        <DimensionCard>
                            <DimensionHeader>
                                <DimensionIcon $color="#f59e0b"><FaChartBar /></DimensionIcon>
                                <DimensionName>Volume</DimensionName>
                            </DimensionHeader>
                            <DimensionScore $color="#f59e0b">{averageScores.volume}</DimensionScore>
                            <DimensionComparison>
                                Team avg: {averageScores.volume} | Class avg: {classAverages.volume}
                            </DimensionComparison>
                        </DimensionCard>
                    </DimensionsGrid>
                </DimensionsGridWrapper>
            </Section>

            <RatingModificationHistory courseId={courseId} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
`;

const LoadingContainer = styled.div`
    padding: 40px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
`;

const NoDataContainer = styled.div`
    padding: 40px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
    background-color: #f9fafb;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
`;

const SummaryCardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const SummaryCard = styled.div`
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding: 14px;
    }
`;

const CardLabel = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const Tooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -100px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    width: 280px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    line-height: 1.5;
    color: #333;
    text-align: left;
`;

const CardValue = styled.div`
    font-size: 36px;
    font-weight: 700;
    color: #3b82f6;
    margin-bottom: 4px;

    @media (max-width: 768px) {
        font-size: 26px;
    }
`;

const CardSubtext = styled.div`
    font-size: 12px;
    color: #9ca3af;
`;

const Section = styled.div`
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
`;

const SectionTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const DimensionTooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -150px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 15px;
    width: 320px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    line-height: 1.6;
    color: #333;
    text-align: left;
`;

const DeadlineText = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 8px 0 16px 0;
`;

const ScoreTableWrapper = styled.div`
    overflow-x: auto;
    margin-top: 8px;
`;

const ScoreTable = styled.table`
    width: 100%;
    min-width: 1080px;
    border-collapse: collapse;
    font-size: 13px;
`;

const ScoreTableHead = styled.thead`
    background-color: #f3f4f6;
`;

const ScoreTableRow = styled.tr`
    border-bottom: 1px solid #e5e7eb;

    &:last-child {
        border-bottom: none;
    }
`;

const ScoreThMember = styled.th`
    text-align: left;
    padding: 10px 12px;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
`;

const ScoreTh = styled.th`
    text-align: center;
    padding: 10px 12px;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
`;

const ScoreTdMember = styled.td`
    text-align: left;
    padding: 10px 12px;
`;

const MemberCellInner = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
`;

const ScoreTd = styled.td`
    text-align: center;
    padding: 10px 12px;
    font-weight: 500;
    color: #374151;
`;

const MemberAvatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
`;

const MemberName = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    text-align: left;
    min-width: 120px;
    flex-shrink: 0;
`;

const CompletedBadge = styled.span`
    color: #22c55e;
    font-weight: 700;
`;

const SortArrow = styled.span`
    margin-left: 4px;
    font-size: 11px;
    opacity: ${props => props.$active ? 1 : 0.35};
    color: ${props => props.$active ? 'currentColor' : '#9ca3af'};
`;

const DimensionsGridWrapper = styled.div`
    overflow-x: auto;
    margin-top: 16px;
`;

const DimensionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    min-width: 760px;
`;

const DimensionCard = styled.div`
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px;
`;

const DimensionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const DimensionIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background-color: ${props => `${props.$color}15`};
    color: ${props => props.$color};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
`;

const DimensionName = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #374151;
`;

const DimensionScore = styled.div`
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.$color};
    margin-bottom: 12px;
`;


const DimensionComparison = styled.div`
    font-size: 12px;
    color: #6b7280;
`;

const ChartLegend = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 12px;
    flex-wrap: wrap;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #374151;
`;

const LegendDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background-color: ${props => props.$color};
    flex-shrink: 0;
`;

const ChartWrapper = styled.div`
    display: flex;
    gap: 0;
    margin-top: 8px;
    overflow-x: auto;
`;

const YAxis = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 56px;
    padding-right: 8px;
    min-width: 24px;
`;

const YLabel = styled.div`
    font-size: 11px;
    color: #9ca3af;
    text-align: right;
    line-height: 1;
`;

const ChartColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ChartArea = styled.div`
    height: 180px;
    position: relative;
`;

const GridLines = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    pointer-events: none;
`;

const GridLine = styled.div`
    border-top: 1px dashed #e5e7eb;
    width: 100%;
`;

const BarsContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 16px;
    height: 100%;
    position: relative;
`;

const BarGroup = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 4px;
    flex: 1;
    min-width: 70px;
    height: 100%;
`;

const Bar = styled.div`
    width: 100%;
    height: ${props => props.$height}%;
    min-height: ${props => props.$height > 0 ? '2px' : '0'};
    background-color: ${props => props.$color};
    border-radius: 3px 3px 0 0;
    cursor: default;
`;

const LabelsRow = styled.div`
    display: flex;
    gap: 16px;
    padding-top: 6px;
`;

const BarLabelGroup = styled.div`
    flex: 1;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding-right: 10px;
    &:last-child {
        padding-right: 0;
    }
`;

const MemberBarAvatar = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 9px;
    flex-shrink: 0;
`;

const BarMemberName = styled.div`
    font-size: 10px;
    color: #6b7280;
    text-align: center;
    white-space: nowrap;
`;

const BarMemberGivenCount = styled.div`
    font-size: 9px;
    color: #9ca3af;
    text-align: center;
    white-space: nowrap;
    margin-top: 1px;
`;

const DistMemberGroup = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex: 1;
    min-width: 120px;
    height: 100%;
    border-right: 1px dashed #e5e7eb;
    padding-right: 10px;
    &:last-child {
        border-right: none;
        padding-right: 0;
    }
`;

const DimGroup = styled.div`
    display: flex;
    align-items: stretch;
    gap: 2px;
    flex: 1;
    height: 100%;
`;

const BarSlot = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
`;

const BarCount = styled.span`
    font-size: 9px;
    font-weight: 600;
    color: #374151;
    line-height: 1;
    margin-bottom: 2px;
`;

const DimScoreLabelsRow = styled.div`
    display: flex;
    gap: 8px;
    width: 100%;
    margin-bottom: 3px;
`;

const DimScoreLabels = styled.div`
    flex: 1;
    display: flex;
    gap: 2px;
`;

const ScoreScaleLabel = styled.span`
    flex: 1;
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
    text-align: center;
`;

export default React.memo(WorkAssessmentResultsPanel);
